import React from "react";
import PropTypes from "prop-types";
import { Box, Paper } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import tinycolor from "tinycolor2";

// Simplified styles from Widget.js
const chatBoxStyles = {
  background: "#fff",
  borderRadius: "16px",
  boxShadow: "0 6px 32px rgba(0,0,0,0.15)",
  border: "1px solid #e0e0e0",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: "550px",
  overflow: "hidden",
  fontFamily: "Arial, sans-serif",
};

const headerStyles = (color) => ({
  background: color,
  color: "#fff",
  padding: "18px 16px 18px 72px",
  fontWeight: "bold",
  fontSize: "18px",
  position: "relative",
  display: "flex",
  alignItems: "center",
  minHeight: "56px",
  flexShrink: 0,
});

const logoCircleStyles = (color) => ({
  position: "absolute",
  left: "16px",
  top: "50%",
  transform: "translateY(-50%)",
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  background: tinycolor(color).darken(10).toHexString(), // Darker shade for contrast
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
  color: "#fff",
  fontWeight: "bold",
  fontSize: "20px",
  textTransform: "uppercase",
});

const avatarImageStyles = {
  position: "absolute",
  left: "16px",
  top: "50%",
  transform: "translateY(-50%)",
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  objectFit: "cover",
  boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
};

const bodyStyles = (color) => ({
  padding: "16px",
  flex: 1,
  overflowY: "auto",
  background: color,
  display: "flex",
  flexDirection: "column",
});

const assistantMsgStyles = (color) => ({
  alignSelf: "flex-start",
  background: "#f1f3f6",
  color: color,
  borderRadius: "18px 18px 18px 6px",
  padding: "12px 18px",
  margin: "6px 0",
  maxWidth: "85%",
  fontSize: "15px",
  wordBreak: "break-word",
  textAlign: "left",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
});

const inputBoxStyles = {
  display: "flex",
  padding: "12px 16px",
  borderTop: "1px solid #e8e8e8",
  background: "#fff",
  gap: "10px",
  alignItems: "center",
};

const inputPlaceholderStyles = {
  flex: 1,
  padding: "12px 18px",
  borderRadius: "24px",
  backgroundColor: "#f1f3f6",
  fontSize: "15px",
  color: "#999",
  outline: "none",
};

const sendBtnStyles = (color) => ({
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  border: "none",
  background: color,
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

function ChatbotPreview({
  name,
  welcomeMessage,
  headerColor,
  buttonColor,
  textColor,
  backgroundColor,
  avatarUrl,
}) {
  const finalHeaderColor = tinycolor(headerColor).isValid() ? headerColor : "#5a49da";
  const finalButtonColor = tinycolor(buttonColor).isValid() ? buttonColor : "#5a49da";
  const finalTextColor = tinycolor(textColor).isValid() ? textColor : "#222";
  const finalBackgroundColor = tinycolor(backgroundColor).isValid() ? backgroundColor : "#f8f9fa";

  return (
    <Paper elevation={0} sx={{ p: 2, border: "1px solid #ddd", borderRadius: 2, height: "100%" }}>
      <Box sx={{ ...chatBoxStyles, background: finalBackgroundColor }}>
        <div style={headerStyles(finalHeaderColor)} id="preview-name-anchor">
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" style={avatarImageStyles} />
          ) : (
            <span style={logoCircleStyles(finalHeaderColor)}>
              {name?.[0]?.toUpperCase() || "C"}
            </span>
          )}
          {name || "Chatbot"}
        </div>
        <div style={bodyStyles(finalBackgroundColor)}>
          {welcomeMessage?.trim() ? (
            <div style={assistantMsgStyles(finalTextColor)} id="preview-welcome-anchor">
              {welcomeMessage.split(/\\r?\\n|\n/).map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          ) : (
            <div style={{ color: "#888", textAlign: "center", marginTop: 12, fontSize: 14 }}>
              Your welcome message will appear here.
            </div>
          )}
        </div>
        <div style={inputBoxStyles}>
          <div style={inputPlaceholderStyles}>Your message...</div>
          <div style={sendBtnStyles(finalButtonColor)}>
            <SendIcon style={{ fontSize: 22, marginLeft: 2 }} />
          </div>
        </div>
      </Box>
    </Paper>
  );
}

ChatbotPreview.propTypes = {
  name: PropTypes.string,
  welcomeMessage: PropTypes.string,
  headerColor: PropTypes.string,
  buttonColor: PropTypes.string,
  textColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  avatarUrl: PropTypes.string,
};

export default ChatbotPreview;
